import React, { useCallback, useEffect, useState } from 'react';
import loadable from '@loadable/component';
import { PaymentSource } from '../../constants';
import { reportError } from '../../lib/reportError';
import {
  authenticate,
  AuthAction,
  AuthErrorMessage,
  AuthAttributes,
  AuthPayloadSuccess,
  AuthError
} from '../../lib/authenticate';
import { loadAmazonLoginScript } from '../../lib/loadScript';
import { loginOnAmazon } from '../../lib/amazon';
import { useCookies } from '../useCookies';
import { useUser } from '../useUser';
import { useErrorModal } from '../useErrorModal';
import { CartUpdateAction, CartUpdatePropertyName, useUpdateCartMutation } from '../useCart';

const ConfirmSocialAccount = loadable(
  () => import('../../components/Authentication/ConfirmSocialAccount')
);
const LinkSocialAccount = loadable(
  () => import('../../components/Authentication/LinkSocialAccount')
);
const SocialAuthModalOverlay = loadable(
  () => import('../../components/Authentication/SocialAuthModalOverlay')
);

export enum SocialAuthProvider {
  Amazon = 'AMAZON'
}

export enum SocialAuthStep {
  Login,
  ConfirmAccount,
  LinkAccounts
}

const ERROR_MESSAGES = {
  AMAZON: 'Amazon authorization failed!'
};

export type ConfirmAccountAttributes = {
  remember: boolean;
  email: string;
  first_name: string;
  last_name: string;
};

export type LinkAccountsAttributes = {
  remember: boolean;
  email: string;
  password: string;
};

export const authenticateAmazon = async (
  action: AuthAction,
  attributes?: Partial<AuthAttributes>
) => {
  const loginResponse = await loginOnAmazon();

  return await authenticate({
    action,
    attributes: {
      access_token: loginResponse.access_token,
      provider: SocialAuthProvider.Amazon.toLowerCase(),
      ...attributes
    }
  });
};

const HANDLERS = {
  [SocialAuthProvider.Amazon]: authenticateAmazon
};

export const useSocialAuth = (
  provider: SocialAuthProvider,
  onAuth?: (user: AuthPayloadSuccess) => void
) => {
  const [step, setStep] = useState(SocialAuthStep.Login);
  const [error, setError] = useState<AuthErrorMessage>('');
  const { setUserData } = useUser();
  const {
    cookies: { cartId }
  } = useCookies();
  const { showErrorModal } = useErrorModal();
  const [updateCart] = useUpdateCartMutation();
  const authenticate = HANDLERS[provider];

  const changePayment = useCallback(
    async (user: AuthPayloadSuccess) => {
      if (user?.isAmazonConnected) {
        try {
          await updateCart(CartUpdateAction.PaymentSource, {
            id: String(user.cartId),
            [CartUpdatePropertyName.PaymentSource]: PaymentSource.Amazon
          });
        } catch (error) {
          reportError('SOCIAL_AUTH_CHANGE_PAYMENT', error as Error);
          showErrorModal();
        }
      }
    },
    [updateCart, showErrorModal]
  );

  const handleLogin = useCallback(async () => {
    try {
      const user = (await authenticate(AuthAction.SignIn, {
        cart_id: cartId
      })) as AuthPayloadSuccess;

      setUserData({ remember: true, isLoggedIn: true, ...user })
        .then(() => onAuth?.(user))
        .then(() => {
          changePayment(user);
        });
    } catch (error) {
      const { code: errorCode } = error as AuthError;

      if (errorCode === 'confirm_account') {
        setStep(SocialAuthStep.ConfirmAccount);
      } else if (errorCode === 'connect_accounts') {
        setStep(SocialAuthStep.LinkAccounts);
      } else {
        reportError('SOCIAL_AUTH_LOGIN', error as Error);
        setError(ERROR_MESSAGES[provider]);
      }
    }
  }, [authenticate, cartId, setUserData, onAuth, changePayment, provider]);

  const handleConfirmAccount = useCallback(
    ({ remember, ...rest }: ConfirmAccountAttributes) =>
      (
        authenticate(AuthAction.Register, {
          cart_id: cartId,
          ...rest
        }) as Promise<AuthPayloadSuccess>
      ).then(user => {
        setUserData({ remember, isLoggedIn: true, ...user }).then(() => onAuth?.(user));
      }),
    [authenticate, setUserData, onAuth, cartId]
  );

  const handleLinkAccounts = useCallback(
    ({ remember, ...rest }: LinkAccountsAttributes) =>
      (
        authenticate(AuthAction.SignIn, {
          link_accounts: true,
          cart_id: cartId,
          ...rest
        }) as Promise<AuthPayloadSuccess>
      ).then(user => {
        setUserData({ remember, isLoggedIn: true, ...user }).then(() => onAuth?.(user));
        changePayment(user);
      }),
    [authenticate, cartId, setUserData, changePayment, onAuth]
  );

  const renderModals = useCallback(
    () => (
      <>
        <SocialAuthModalOverlay
          onClose={() => setStep(SocialAuthStep.Login)}
          show={step === SocialAuthStep.ConfirmAccount}
        >
          <ConfirmSocialAccount
            provider={provider}
            onSubmit={handleConfirmAccount}
            onSignInClick={() => setStep(SocialAuthStep.LinkAccounts)}
          />
        </SocialAuthModalOverlay>

        <SocialAuthModalOverlay
          onClose={() => setStep(SocialAuthStep.Login)}
          show={step === SocialAuthStep.LinkAccounts}
        >
          <LinkSocialAccount
            provider={provider}
            onSubmit={handleLinkAccounts}
            onChangeEmailClick={() => setStep(SocialAuthStep.ConfirmAccount)}
          />
        </SocialAuthModalOverlay>
      </>
    ),
    [handleConfirmAccount, handleLinkAccounts, provider, step]
  );

  useEffect(() => {
    switch (provider) {
      case SocialAuthProvider.Amazon:
        return loadAmazonLoginScript();
    }
  }, [provider]);

  return {
    step,
    setStep,
    error,
    handleLogin,
    handleConfirmAccount,
    handleLinkAccounts,
    renderModals
  };
};
